/**
 * Lane C0-PROPORTION (B3 port, behind figureB3.ts `FIGURE_B3`): the head items the shrink cannot carry, redrawn at the
 * B3 head size (×0.75): the cloth mask and the douli's veil (headwear.ts `mask`, `veil`: their slits and evenly spaced
 * threads break up when shrunk) and the starter's hair ornaments (outfit2x/ornaments.ts: the man's gold crown, the
 * woman's plum-flower hairpin), the same materials and lighting, a quarter smaller.
 */
import type { View } from '../../sprites';
import type { Layer, Piece } from '../figure';
import type { Skeleton } from '../skeleton';
import type { NativeCtx } from '../outfit2x/native';
import { type Mat, mat, tone } from '../outfit2x/paint';

type Grid = { ox: number; oy: number; rows: readonly string[] };

// ── mask and veil (art pixels, drawn on the head joint as headwear.ts does) ──

const MASK: Partial<Record<View, Grid>> = {
  front: { ox: 5, oy: -3, rows: ['mmmmmmmmmm', 'mmmmmmmmmm', '.mmmmmmmm.', '..mmmmmm..', '...MMMM...'] },
  front3: { ox: 3, oy: -3, rows: ['mmmmmmmmM', 'mmmmmmmmM', '.mmmmmmmm', '..mmmmmm.', '....MMM..'] },
  side: { ox: -2, oy: -3, rows: ['mmmmM', 'mmmmM', '.mmmm', '.mmm.', '..mm.'] },
};
const MASK_COLOURS: Record<string, string> = { m: '#2a2438', M: '#1a1828' };

/** Every third thread, 22 wide (29 at today's size), from the brim down past the chin. */
const VEIL_ROWS = Array.from({ length: 10 }, () => 'v..'.repeat(7) + 'v');
const VEIL: Grid = { ox: 11, oy: 2, rows: VEIL_ROWS };

function gridLayer(id: string, grid: (view: View) => Grid | undefined, colours: Record<string, string>, z: (sk: Skeleton) => number): Layer {
  return {
    id: `headwear:${id}`,
    label: `${id} (B3)`,
    pieces(sk) {
      const g0 = grid(sk.view);
      if (!g0) return [];
      const h = sk.joints.head;
      const x = Math.round(h.x);
      const y = Math.round(h.y);
      return [
        {
          z: z(sk),
          draw: (g) => {
            g0.rows.forEach((row, r) => {
              for (let c = 0; c < row.length; c++) {
                const col = colours[row[c]!];
                if (!col) continue;
                g.fillStyle = col;
                g.fillRect(x - g0.ox + c, y - g0.oy + r, 1, 1);
              }
            });
          },
        },
      ];
    },
  };
}

/** The B3 mask (unseen from behind, as today). */
export const maskB3 = (): Layer =>
  gridLayer('mask', (v) => MASK[v], MASK_COLOURS, (sk) => sk.joints.head.z + 0.03);

/** The B3 veil, all round. */
export const veilB3 = (): Layer => gridLayer('veil', () => VEIL, { v: '#c8c2b6' }, (sk) => sk.joints.head.z + 0.065);

/** Headwear ids drawn by this file instead of being shrunk. */
export const REDRAWN_HEADWEAR: Readonly<Record<string, () => Layer>> = { mask: maskB3, veil: veilB3 };

// ── crown and hairpin (canvas pixels, painted natively as ornaments.ts does) ──

interface Stamp {
  rows: readonly string[];
  ax: number;
  ay: number;
}

const MATS: Record<string, Mat> = {
  g: mat('#d6a640', 'gold'),
  j: mat('#6cc0a0', 'jade'),
  w: mat('#e4ece0', 'jade'),
  r: mat('#b8283a', 'silk'),
  p: mat('#e87a96', 'silk'),
};

const CROWN: Stamp = { rows: ['..g..g..', '..gggg..', 'wwgggGww', '..gGGg..', '...GG...'], ax: 4, ay: 4 };
const CROWN_SIDE: Stamp = { rows: ['.g.g..', '.gggg.', 'wwgGww', '.gGGg.', '..GG..'], ax: 3, ay: 4 };
const CROWN_BACK: Stamp = { rows: ['ww....ww'], ax: 4, ay: 1 };
const HAIRPIN: Stamp = {
  rows: ['..pp....', '.pPPp...', 'pPggPp..', '.pPPpg..', '..pp.g..', '.g...g.g', 'g....r.g', '.....g.r'],
  ax: 3,
  ay: 2,
};

/** ornaments.ts `layStamp`: the stamp's light from its own shape. */
function layStamp(c: NativeCtx, s: Stamp, X: number, Y: number, flip = false): void {
  const H = s.rows.length;
  const W = Math.max(...s.rows.map((r) => r.length));
  const ch = (u: number, v: number) => (v < 0 || v >= H || u < 0 || u >= W ? '.' : (s.rows[v]![flip ? W - 1 - u : u] ?? '.'));
  const key = (q: string) => q.toLowerCase();
  for (let v = 0; v < H; v++)
    for (let u = 0; u < W; u++) {
      const q = ch(u, v);
      const m = MATS[key(q)];
      if (q === '.' || !m) continue;
      const same = (du: number, dv: number) => key(ch(u + du, v + dv)) === key(q);
      let lv = q === key(q) ? 0 : -1;
      if (!same(c.light, 0)) lv += 1;
      else if (!same(-c.light, 0)) lv -= 1;
      if (!same(0, -1) && lv >= 0) lv = Math.min(2, lv + 1);
      if (!same(0, 1) && lv <= 0) lv -= 1;
      c.lay(X + u - (flip ? W - 1 - s.ax : s.ax), Y + v - s.ay, tone(m.ramp, lv), 0.001);
    }
}

function ornament(ax: number, ay: number, z: number, paint: (c: NativeCtx) => void): Piece {
  return {
    z,
    draw: (g) => {
      g.fillStyle = 'rgba(214, 166, 64, 0.004)';
      g.fillRect(Math.round(ax), Math.round(ay), 1, 1);
    },
    native: paint,
  };
}

/** The crown on the (shrunk) hair's crown anchor `a` (art px from the head joint, already at B3 scale). */
export function crownB3(sk: Skeleton, a: readonly [number, number]): Piece {
  const h = sk.joints.head;
  const st = sk.back ? CROWN_BACK : sk.view === 'side' ? CROWN_SIDE : CROWN;
  return ornament(h.x + a[0], h.y + a[1], h.z + 0.04, (c) => layStamp(c, st, c.X(h.x + a[0]) + 1, c.Y(h.y + a[1]) + 1));
}

/** The hairpin on the (shrunk) bun anchor `a`. */
export function hairpinB3(sk: Skeleton, a: readonly [number, number]): Piece {
  const h = sk.joints.head;
  return ornament(h.x + a[0], h.y + a[1], h.z + 0.045, (c) =>
    layStamp(c, HAIRPIN, c.X(h.x + a[0]) + 1, c.Y(h.y + a[1]) + 1, sk.back),
  );
}
